import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import React, { useEffect, useState } from "react";
import ListItemText from "@mui/material/ListItemText";
import { useSelector, useDispatch } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getRequest, postRequest, showMessage } from "../../../../config.js";
import { setCompanies } from "../../../../reducers/companiesReducer.js";

export default function TempPermissionsClient({ profileClass, advisors, setRefresh, company }) {
    const user = useSelector(state => state.user);
    const companies = useSelector(state => state.companies);
    const dispatch = useDispatch();

    const [allCompanies, setAllCompanies] = useState([]);
    const [tempModules, setTempModules] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    // Flat list of all TempSections, each enriched with .permission map (mirrors PermissionsClient)
    const [sections, setSections] = useState([]);
    const [openAccordions, setOpenAccordions] = useState({});

    // ─── Flatten companies (parent + children) — identical to PermissionsClient ───
    useEffect(() => {
        if (!companies?.length) return;
        const seen = new Set();
        const flat = [];
        for (const comp of companies) {
            const copy = { ...comp };
            delete copy.childrenCompanies;
            if (!seen.has(comp._id)) { flat.push(copy); seen.add(comp._id); }
            if (Array.isArray(comp.childrenCompanies)) {
                for (const child of comp.childrenCompanies) {
                    const childCopy = { ...child };
                    delete childCopy.childrenCompanies;
                    if (!seen.has(child._id)) { flat.push(childCopy); seen.add(child._id); }
                }
            }
        }
        setAllCompanies(flat);
    }, [companies]);

    // ─── Fetch all TempModules + Checklists with their sections ──────────────────
    useEffect(() => {
        getRequest('/modules/all-modules-section', 'not-showMessage').then(res => {
            if (res?.code === 200) {
                const body = res.body;
                const combined = Array.isArray(body)
                    ? body
                    : [
                        ...(body?.modules || []).filter(m => m.sections?.length > 0),
                        ...(body?.checklists || []).filter(m => m.sections?.length > 0).map(m => ({ ...m, isChecklist: true })),
                    ];
                setTempModules(combined);
            }
        }).finally(() => setIsLoading(false));
    }, []);

    // ─── Build sections with permission data (mirrors PermissionsClient logic) ────
    // Each section gets: { sectionNo, sectionName, moduleId, moduleName, moduleKey,
    //   permission: { [companyId]: { [advisorId]: advisorDoc } } }
    useEffect(() => {
        if (!tempModules.length || !allCompanies.length) return;

        // Flatten all TempSections annotated with their parent module info
        const flatSections = tempModules.flatMap(m =>
            m.sections.map(s => ({
                ...s,
                moduleId: m._id,
                moduleName: m.moduleName,
                moduleKey: m.moduleKey,
            }))
        );

        const sectionObj = JSON.parse(JSON.stringify(flatSections));
        const userType = user.role === 'Advisor' ? 'advisors' : 'investors';

        for (const comp of allCompanies) {
            if (!comp[userType]) continue;
            for (const advisorId of Object.keys(comp[userType])) {
                const advisorSettings = comp[userType][advisorId];
                const advisor = advisors.find(({ _id }) => _id == advisorId);
                if (!advisorSettings?.modules || !advisor) continue;

                for (const [key, value] of Object.entries(advisorSettings.modules)) {
                    if (!value) continue;
                    const idx = sectionObj.findIndex(s => s.sectionNo === key);
                    if (idx >= 0) {
                        if (!sectionObj[idx].permission) sectionObj[idx].permission = {};
                        if (!sectionObj[idx].permission[comp._id]) sectionObj[idx].permission[comp._id] = {};
                        sectionObj[idx].permission[comp._id][advisorId] = advisor;
                    }
                }
            }
        }
        setSections(sectionObj);
    }, [allCompanies, tempModules]);

    const toggleAccordion = (moduleId) => {
        setOpenAccordions(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
    };

    // ─── Single-section permission toggle (mirrors PermissionsClient.handlePermissions) ──
    const handlePermissions = async (e, advisor, sectionNo, comp) => {
        const status = e.target.checked;
        const res = await postRequest(`/user/advisorPermissions/${comp._id}`, {
            advisor,
            company: comp,
            advisorId: advisor._id,
            sectionNo,
            status,
            firmId: user.companyId._id,
        });

        const userType = advisor.role === 'Advisor' ? 'advisors' : 'investors';

        if (res?.code === 200) {
            const sectionObj = JSON.parse(JSON.stringify(sections));
            const sectionIndex = sectionObj.findIndex(s => s.sectionNo === sectionNo);
            const companyId = comp._id;
            const advisorId = advisor._id;

            if (status) {
                if (!sectionObj[sectionIndex].permission || !sectionObj[sectionIndex].permission[companyId]) {
                    sectionObj[sectionIndex] = {
                        ...sectionObj[sectionIndex],
                        permission: { [companyId]: null },
                    };
                }
                sectionObj[sectionIndex].permission[companyId] = {
                    ...sectionObj[sectionIndex].permission[companyId],
                    [advisorId]: {
                        ...advisor,
                        permissions: {
                            ...(comp[userType]?.[advisorId]?.modules),
                            [sectionNo]: status,
                        },
                    },
                };
            } else {
                delete sectionObj[sectionIndex].permission?.[companyId]?.[advisorId];
            }

            const updatedData = allCompanies.map(item => {
                if (item._id === companyId) {
                    return {
                        ...item,
                        [userType]: {
                            ...item[userType],
                            [advisorId]: {
                                ...item[userType]?.[advisorId],
                                modules: {
                                    ...item[userType]?.[advisorId]?.modules,
                                    [sectionNo]: status,
                                },
                            },
                        },
                    };
                }
                return item;
            });
            dispatch(setCompanies(updatedData));
            setSections(sectionObj);
            showMessage(res.message);
            setRefresh(r => !r);
        }
    };

    // ─── Bulk-toggle all temp sections for an advisor on a company ────────────────
    const handleMultiPermissions = async (e, advisor, sectionNos, comp) => {
        const _checked = e.target.checked;
        const result = await postRequest(`/user/advisorMultiPermissions/${comp._id}`, {
            advisor,
            company: comp,
            advisorId: advisor._id,
            sectionNo: sectionNos,
            status: _checked,
            firmId: user.companyId._id,
        });

        if (result?.code === 200) {
            const sectionObj = JSON.parse(JSON.stringify(sections));
            sectionObj.forEach((section, sectionIndex) => {
                const advisorId = advisor._id;
                if (_checked) {
                    if (sectionObj[sectionIndex].permission?.[comp._id]) {
                        if (!sectionObj[sectionIndex].permission[comp._id][advisorId]) {
                            sectionObj[sectionIndex].permission[comp._id] = {
                                ...sectionObj[sectionIndex].permission[comp._id],
                                [advisorId]: advisor,
                            };
                        }
                    } else {
                        sectionObj[sectionIndex] = {
                            ...sectionObj[sectionIndex],
                            permission: {
                                ...sectionObj[sectionIndex].permission,
                                [comp._id]: { [advisorId]: advisor },
                            },
                        };
                    }
                } else {
                    delete sectionObj[sectionIndex].permission?.[comp._id]?.[advisorId];
                }
            });
            setSections(sectionObj);

            // Keep allCompanies in Redux in sync so that subsequent single-section
            // permission changes don't overwrite this bulk update with stale data.
            const userType = advisor.role === 'Advisor' ? 'advisors' : 'investors';
            const currentModules = allCompanies
                .find(c => c._id === comp._id)?.[userType]?.[advisor._id]?.modules || {};
            const newModules = { ...currentModules };
            sectionNos.forEach(sno => { newModules[sno] = _checked; });
            const updatedCompanies = allCompanies.map(item => {
                if (item._id === comp._id) {
                    return {
                        ...item,
                        [userType]: {
                            ...item[userType],
                            [advisor._id]: {
                                ...item[userType]?.[advisor._id],
                                modules: newModules,
                            },
                        },
                    };
                }
                return item;
            });
            dispatch(setCompanies(updatedCompanies));
        }
        showMessage(result.message);
        setRefresh(r => !r);
    };

    // Returns true if an advisor has all temp sections granted for a given company
    const checkAdvisorHasAllAllocations = ({ advisor, company: comp }) => {
        const userType = user.role === 'Advisor' ? 'advisors' : 'investors';
        if (advisor && comp?.[userType]?.[advisor._id]?.modules) {
            const allTempSectionNos = sections.map(s => s.sectionNo);
            if (!allTempSectionNos.length) return false;
            const advisorPermissions = Object.entries(comp[userType][advisor._id].modules)
                .filter(([, val]) => val === true)
                .map(([key]) => key);
            return allTempSectionNos.every(no => advisorPermissions.includes(no));
        }
        return false;
    };

    // ─── Empty / loading states ───────────────────────────────────────────────────
    if (!allCompanies.length) {
        return (
            <div className="df jcc c15 fs1-75rem fw500 lh2 mt2rem">
                No clients added for permissions
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="sectionWrapper p1rem fs0-75rem pr mr2rem ml1rem pr">
                <div className="table-permission">
                    <table id="table-temp-permissions" className="table-userPermissions table-clientPermissions">
                        <thead className="ps t0">
                            <tr>
                                <th className="index-table"></th>
                                {allCompanies.map((_, cIndex) => (
                                    <th className="user-area" key={`skel-comp-${cIndex}`}>
                                        <div className="df aic">
                                            <div className="mr1rem wfull">
                                                <Skeleton variant="text" width={110} height={22} />
                                                <Skeleton variant="text" width={58} height={16} />
                                            </div>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3].map(mIdx => (
                                <React.Fragment key={`skel-m-${mIdx}`}>
                                    <tr style={{ background: '#f8f9fa' }}>
                                        <td style={{ padding: '10px 12px', paddingTop: mIdx > 1 ? '20px' : '10px', borderTop: mIdx > 1 ? '1px solid #DBDFEE' : 'none' }}>
                                            <div className="df aic" style={{ gap: '8px' }}>
                                                <Skeleton variant="circular" width={22} height={22} />
                                                <Skeleton variant="rectangular" width={120 + mIdx * 25} height={18} sx={{ borderRadius: '4px' }} />
                                                <Skeleton variant="rectangular" width={48} height={18} sx={{ borderRadius: '4px' }} />
                                                <Skeleton variant="rectangular" width={72} height={16} sx={{ borderRadius: '4px' }} />
                                            </div>
                                        </td>
                                        {allCompanies.map((_, i) => (
                                            <td key={i} style={{ background: '#f8f9fa', borderTop: mIdx > 1 ? '1px solid #DBDFEE' : 'none' }} />
                                        ))}
                                    </tr>
                                    {mIdx === 1 && [1, 2, 3].map(sIdx => (
                                        <tr key={`skel-s-${sIdx}`}>
                                            <td style={{ background: '#fafbfe', padding: '8px 12px' }}>
                                                <div className="df aic" style={{ gap: '6px' }}>
                                                    <Skeleton variant="rectangular" width={58} height={14} sx={{ borderRadius: '3px' }} />
                                                    <Skeleton variant="rectangular" width={90 + sIdx * 25} height={14} sx={{ borderRadius: '3px' }} />
                                                </div>
                                            </td>
                                            {allCompanies.map((_, cIdx) => (
                                                <td key={`skel-sc-${cIdx}`} className="cp">
                                                    <div className="w100 df jcsb aic bg1 p0-5rem br30px" style={{ border: '1px solid #DBDFEE' }}>
                                                        <Skeleton variant="circular" width={32} height={32} />
                                                        <Skeleton variant="circular" width={20} height={20} />
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    if (!tempModules.length) {
        return (
            <div className="df jcc c15 fs1-25rem fw500 lh2 mt2rem">
                No modules available
            </div>
        );
    }

    // Group the flat sections list back into per-module buckets for accordion rendering,
    // preserving the order returned by the API
    const sectionsByModule = tempModules
        .map(module => ({
            module,
            moduleSections: sections.filter(s => String(s.moduleId) === String(module._id)),
        }))
        .filter(({ moduleSections }) => moduleSections.length > 0);

    const moduleItems = sectionsByModule.filter(({ module }) => !module.isChecklist);
    const checklistItems = sectionsByModule.filter(({ module }) => module.isChecklist);

    const allTempSectionNos = sections.map(s => s.sectionNo);

    return (
        <div className="sectionWrapper p1rem fs0-75rem pr mr2rem ml1rem pr">
            <div className="table-permission">
                <table id="table-temp-permissions" className="table-userPermissions table-clientPermissions">

                    {/* ── Header: company names + bulk-select dropdown (same as PermissionsClient) ── */}
                    <thead className="ps t0">
                        <tr>
                            <th className="index-table"></th>
                            {allCompanies.map((comp, cIndex) => (
                                <th className="user-area" key={`temp-comp-${cIndex}`}>
                                    <div className="df aic">
                                        <div className="mr1rem wfull pr">
                                            <span className="di fs1rem c0">{comp.cName}</span>
                                            {comp.subscription ? (
                                                <span className="c7 di ml0-5rem">
                                                    ({comp.subscription?.toUpperCase()})
                                                </span>
                                            ) : (
                                                <span className="c8 di ml0-5rem">(INACTIVE)</span>
                                            )}
                                            {/* Bulk-allocate all temp sections for an advisor */}
                                            <label className="">
                                                <Select
                                                    multiple
                                                    className="select-advisor w10 pa"
                                                    id={`bulk-select-${cIndex}`}
                                                    sx={{ "& .MuiSvgIcon-root": { color: "#4A67FF" } }}
                                                    IconComponent={ExpandMoreIcon}
                                                    value={[]}
                                                    MenuProps={{
                                                        anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
                                                        transformOrigin: { vertical: 'top', horizontal: 'right' },
                                                    }}
                                                >
                                                    {advisors.map((advisor, idx) => (
                                                        <MenuItem
                                                            key={`${cIndex}-bulk-${idx}`}
                                                            value={advisor}
                                                            className="select-advisor-list ttc"
                                                        >
                                                            <Checkbox
                                                                checked={checkAdvisorHasAllAllocations({ advisor, company: comp })}
                                                                onClick={(e) =>
                                                                    handleMultiPermissions(e, advisor, allTempSectionNos, comp)
                                                                }
                                                            />
                                                            <ListItemText
                                                                primary={`${advisor.firstName} ${advisor.lastName} - ${advisor.title || advisor.designation || advisor.job || ''}`}
                                                            />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </label>
                                        </div>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {moduleItems.length > 0 && (
                            <>
                                <tr>
                                    <td colSpan={allCompanies.length + 1} style={{ padding: '16px 0 8px', borderBottom: '2px solid #4A67FF' }}>
                                        <span className="fw600 c3 fs1-25rem">Modules</span>
                                    </td>
                                </tr>
                                {moduleItems.map(({ module, moduleSections }, mIdx) => (
                                    <React.Fragment key={`m-${module._id}`}>
                                        <tr onClick={() => toggleAccordion(module._id)} style={{ background: '#f8f9fa', cursor: 'pointer' }}>
                                            <td style={{ padding: '10px 12px', paddingTop: mIdx > 0 ? '20px' : '10px', borderTop: mIdx > 0 ? '1px solid #DBDFEE' : 'none', width: '100% !important', minWidth: '100%!important' }}>
                                                <div className="df aic jcsb w100">
                                                    <div className="df aic" style={{ gap: '8px' }}>
                                                        <ExpandMoreIcon className="c3 td250" style={{ fontSize: '22px', transform: openAccordions[module._id] ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                                                        <span className="fw600 c3 fs1rem">{module.moduleName}</span>
                                                        {module.moduleKey && <span className="fs0-75rem c15 px0-5rem py0-25rem br5px" style={{ background: '#e9ecef', opacity: 0.8 }}>{module.moduleKey}</span>}
                                                        <span className="fs0-75rem" style={{ color: '#888' }}>({moduleSections.length} section{moduleSections.length !== 1 ? 's' : ''})</span>
                                                    </div>
                                                </div>
                                            </td>
                                            {allCompanies.map((_, i) => <td key={i} style={{ background: '#f8f9fa', borderTop: mIdx > 0 ? '1px solid #DBDFEE' : 'none' }} />)}
                                        </tr>
                                        {openAccordions[module._id] && moduleSections.map((section, sIdx) => (
                                            <tr key={section.sectionNo}>
                                                <td className="c15 bg0" style={{ background: '#fafbfe' }}>
                                                    <span className="fw600 bg0" style={{ marginRight: '4px' }}>{section.sectionNo}</span>
                                                    {section.sectionName}
                                                </td>
                                                {allCompanies.map((comp, cIdx) => {
                                                    const isCompanyLocked = module.isMainModule && !module.companies?.[comp._id];
                                                    return (
                                                        <td className="c15 cp" key={`${cIdx}-check-${section.sectionNo}-${sIdx}`} style={isCompanyLocked ? { opacity: 0.4, pointerEvents: 'none' } : {}}>
                                                            <div className="w100 df jcsb bg1 p0-5rem br30px" style={{ border: '1px solid #DBDFEE' }}>
                                                                <div className="df">
                                                                    {!isCompanyLocked && section.permission?.[comp._id] && Object.values(section.permission[comp._id]).map((advisor, keyIndex) => (
                                                                        advisor?.profile ? (
                                                                            <img key={`${keyIndex}-av`} src={advisor.profile} className={`w2rem h2rem df jcc aic br100 mr0-5rem pr ${profileClass}`} data-tooltip={advisor.firstName ? `${advisor.firstName} ${advisor.lastName}` : 'Register to show your name here'} alt="" />
                                                                        ) : (
                                                                            <span key={`${keyIndex}-av-init`} data-tooltip={advisor.firstName ? `${advisor.firstName} ${advisor.lastName}` : 'Register to show your name here'} className={`w2rem h2rem df jcc aic br100 mr0-5rem pr ${profileClass}`}>{advisor.firstName ? `${advisor.firstName[0]}${advisor.lastName[0]}` : '?'}</span>
                                                                        )
                                                                    ))}
                                                                </div>
                                                                <div className="pr">
                                                                    <Select multiple className="select-advisor w10" id={`section-select-${comp._id}-${section.sectionNo}`} sx={{ "& .MuiSvgIcon-root": { color: "#4A67FF" } }} IconComponent={ExpandMoreIcon} value={[]} disabled={isCompanyLocked} MenuProps={{ PaperProps: { style: { maxHeight: 300, width: 'auto', minWidth: '200px' } }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null, keepMounted: true, marginThreshold: 10, style: { WebkitTransform: 'none', position: 'fixed' } }}>
                                                                        {advisors.map((advisor, adIdx) => (
                                                                            <MenuItem key={`${section.sectionNo}-${comp._id}-${adIdx}`} value={advisor._id} className="select-advisor-list ttc">
                                                                                <Checkbox checked={!!section.permission?.[comp._id]?.[advisor._id]} onChange={(e) => handlePermissions(e, advisor, section.sectionNo, comp)} />
                                                                                <ListItemText primary={`${advisor.firstName} ${advisor.lastName} - ${advisor.title || advisor.designation || advisor.job || ''}`} />
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </>
                        )}
                        {checklistItems.length > 0 && (
                            <>
                                <tr>
                                    <td colSpan={allCompanies.length + 1} style={{ padding: '32px 0 8px', borderBottom: '2px solid #4A67FF' }}>
                                        <span className="fw600 c3 fs1-25rem">Checklists</span>
                                    </td>
                                </tr>
                                {checklistItems.map(({ module, moduleSections }, mIdx) => (
                                    <React.Fragment key={`c-${module._id}`}>
                                        <tr onClick={() => toggleAccordion(module._id)} style={{ background: '#f8f9fa', cursor: 'pointer' }}>
                                            <td style={{ padding: '10px 12px', paddingTop: mIdx > 0 ? '20px' : '10px', borderTop: mIdx > 0 ? '1px solid #DBDFEE' : 'none', width: '100% !important', minWidth: '100%!important' }}>
                                                <div className="df aic jcsb w100">
                                                    <div className="df aic" style={{ gap: '8px' }}>
                                                        <ExpandMoreIcon className="c3 td250" style={{ fontSize: '22px', transform: openAccordions[module._id] ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                                                        <span className="fw600 c3 fs1rem">{module.moduleName}</span>
                                                        {module.moduleKey && <span className="fs0-75rem c15 px0-5rem py0-25rem br5px" style={{ background: '#e9ecef', opacity: 0.8 }}>{module.moduleKey}</span>}
                                                        <span className="fs0-75rem" style={{ color: '#888' }}>({moduleSections.length} section{moduleSections.length !== 1 ? 's' : ''})</span>
                                                    </div>
                                                </div>
                                            </td>
                                            {allCompanies.map((_, i) => <td key={i} style={{ background: '#f8f9fa', borderTop: mIdx > 0 ? '1px solid #DBDFEE' : 'none' }} />)}
                                        </tr>
                                        {openAccordions[module._id] && moduleSections.map((section, sIdx) => (
                                            <tr key={section.sectionNo}>
                                                <td className="c15 bg0" style={{ background: '#fafbfe' }}>
                                                    <span className="fw600 bg0" style={{ marginRight: '4px' }}>{section.sectionNo}</span>
                                                    {section.sectionName}
                                                </td>
                                                {allCompanies.map((comp, cIdx) => {
                                                    const isCompanyLocked = module.isMainModule && !module.companies?.[comp._id];
                                                    return (
                                                        <td className="c15 cp" key={`${cIdx}-cl-check-${section.sectionNo}-${sIdx}`} style={isCompanyLocked ? { opacity: 0.4, pointerEvents: 'none' } : {}}>
                                                            <div className="w100 df jcsb bg1 p0-5rem br30px" style={{ border: '1px solid #DBDFEE' }}>
                                                                <div className="df">
                                                                    {!isCompanyLocked && section.permission?.[comp._id] && Object.values(section.permission[comp._id]).map((advisor, keyIndex) => (
                                                                        advisor?.profile ? (
                                                                            <img key={`${keyIndex}-cl-av`} src={advisor.profile} className={`w2rem h2rem df jcc aic br100 mr0-5rem pr ${profileClass}`} data-tooltip={advisor.firstName ? `${advisor.firstName} ${advisor.lastName}` : 'Register to show your name here'} alt="" />
                                                                        ) : (
                                                                            <span key={`${keyIndex}-cl-av-init`} data-tooltip={advisor.firstName ? `${advisor.firstName} ${advisor.lastName}` : 'Register to show your name here'} className={`w2rem h2rem df jcc aic br100 mr0-5rem pr ${profileClass}`}>{advisor.firstName ? `${advisor.firstName[0]}${advisor.lastName[0]}` : '?'}</span>
                                                                        )
                                                                    ))}
                                                                </div>
                                                                <div className="pr">
                                                                    <Select multiple className="select-advisor w10" id={`cl-section-select-${comp._id}-${section.sectionNo}`} sx={{ "& .MuiSvgIcon-root": { color: "#4A67FF" } }} IconComponent={ExpandMoreIcon} value={[]} disabled={isCompanyLocked} MenuProps={{ PaperProps: { style: { maxHeight: 300, width: 'auto', minWidth: '200px' } }, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null, keepMounted: true, marginThreshold: 10, style: { WebkitTransform: 'none', position: 'fixed' } }}>
                                                                        {advisors.map((advisor, adIdx) => (
                                                                            <MenuItem key={`cl-${section.sectionNo}-${comp._id}-${adIdx}`} value={advisor._id} className="select-advisor-list ttc">
                                                                                <Checkbox checked={!!section.permission?.[comp._id]?.[advisor._id]} onChange={(e) => handlePermissions(e, advisor, section.sectionNo, comp)} />
                                                                                <ListItemText primary={`${advisor.firstName} ${advisor.lastName} - ${advisor.title || advisor.designation || advisor.job || ''}`} />
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </>
                        )}
                    </tbody>

                </table>
            </div>
        </div>
    );
}
